import { I18n } from "@assets/strings";
import { Range } from "immutable";
import React, { useMemo } from "react";
import styled from "styled-components";
import { gap } from "@ui/styles/utils";
import { PrimaryMedium } from "@ui/components/containers/primary";
import { Selecter } from "./selecter";

interface DatePickerProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
	value?: Date | null;
	onChange: (date: Date) => void;
	minYear: number;
	maxYear?: number;
	label?: string;
	hideMonth?: boolean;
	hideDay?: boolean;
	customStyle?: boolean;
}
export const DatePicker: React.FC<DatePickerProps> = ({
	value,
	onChange,
	label,
	minYear,
	maxYear,
	hideMonth,
	hideDay = true,
	customStyle,
	...htmlProps
}) => {
	const currentYear = useMemo(() => new Date().getFullYear(), []);
	const currentMonth = useMemo(() => new Date().getMonth(), []);

	function getDaysInMonth(year?: number, month?: number): number {
		return new Date(year ?? currentYear, (month ?? currentMonth) + 1, 0).getDate();
	}

	return (
		<Container {...htmlProps}>
			{label && <Label>{label}</Label>}
			<Content>
				{!hideMonth && (
					<Selecter
						placeholder={I18n.t("month")}
						value={value?.getMonth().toString()}
						onChange={(v) => onChange(new Date((value ?? new Date()).getFullYear(), +v, value?.getDate() ?? 15))}
						options={Range(0, 12)
							.map((m) => new Date(1970, m, 15))
							.map((date) => ({
								value: date.getMonth().toString(),
								label: date.toLocaleString("default", { month: "long" }),
							}))
							.toArray()}
						customStyle={customStyle}
					/>
				)}
				{!hideDay && (
					<Selecter
						placeholder={I18n.t("day")}
						value={value?.getDate().toString()}
						onChange={(v) => {
							onChange(new Date((value ?? new Date()).getFullYear(), (value ?? new Date()).getMonth(), +v));
						}}
						options={Range(1, getDaysInMonth(value?.getFullYear(), value?.getMonth()) + 1)
							.map((day) => ({
								label: day.toString(),
								value: day.toString(),
							}))
							.toArray()}
						customStyle={customStyle}
					/>
				)}
				<Selecter
					placeholder={I18n.t("year")}
					value={value?.getFullYear().toString()}
					onChange={(v) =>
						onChange(new Date(new Date(v).getFullYear(), (value ?? new Date()).getMonth(), value?.getDate() ?? 15))
					}
					options={Range(minYear, (maxYear ?? currentYear) + 1)
						.reverse()
						.map((y) => ({
							value: y.toString(),
							label: y.toString(),
						}))
						.toArray()}
					customStyle={customStyle}
				/>
			</Content>
		</Container>
	);
};

const Container = styled.div``;

const Label = styled(PrimaryMedium)`
	margin-bottom: 5px;
`;

const Content = styled.div`
	display: flex;
	${gap(16, "row")}

	> * {
		flex: 1;
	}
`;
